/**
 * Custom Quick Add
 * ----------------------------------------------------------------------
 * Desktop: hovering the product image reveals a "Quick Buy" bar; hovering
 * further into that bar reveals the row of size blocks (pure CSS, no JS).
 *
 * Touch devices (no hover): reveals happen on touchstart (the instant a
 * finger touches the screen), not on a completed tap — this mimics the
 * immediacy of a mouse hover. Touching the product image reveals the bar;
 * touching the bar reveals the size row. The click that naturally follows
 * a touch is only used to stop the image from navigating away on that
 * very first touch — once revealed, a second touch on the image navigates
 * normally. Tapping a size adds it to the cart.
 *
 * The store's cart drawer is provided by a third-party app (Opus), which
 * auto-detects standard /cart/add.js requests and opens itself — so this
 * script intentionally does NOT try to open any drawer manually. It just
 * performs a clean, standard add-to-cart request.
 */
(function () {
  var ADD_URL = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  var isTouch = window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;

  function resetAllExcept(exceptRoot) {
    document.querySelectorAll('.custom-quick-add--peek, .custom-quick-add--open').forEach(function (root) {
      if (root !== exceptRoot) {
        root.classList.remove('custom-quick-add--peek');
        root.classList.remove('custom-quick-add--open');
        delete root.dataset.cqaJustPeeked;
      }
    });
  }

  // --- Touch: react the instant a finger touches the screen ---

  function handleTouchStart(evt) {
    if (!isTouch) return;

    var bar = evt.target.closest('[data-cqa-bar]');
    if (bar) {
      var barRoot = bar.closest('[data-custom-quick-add]');
      if (barRoot && !barRoot.classList.contains('custom-quick-add--open')) {
        resetAllExcept(barRoot);
        barRoot.classList.add('custom-quick-add--peek');
        barRoot.classList.add('custom-quick-add--open');
      }
      return;
    }

    var link = evt.target.closest('.image-cont .product-link');
    if (!link) return;

    var imageCont = link.closest('.image-cont');
    var root = imageCont && imageCont.querySelector('[data-custom-quick-add]');
    if (!root) return;

    if (!root.classList.contains('custom-quick-add--peek')) {
      resetAllExcept(root);
      root.classList.add('custom-quick-add--peek');
      // NOTE: no longer blocks the click that follows — the image link
      // navigates to the product on a single tap/click. The bar is still
      // shown for anyone who wants to tap a size instead of the image.
    }
  }

  // --- Click: only used to stop navigation on that very first touch,
  //     and as the fallback interaction model on mouse/desktop ---

  function handleBarClick(evt) {
    var bar = evt.target.closest('[data-cqa-bar]');
    if (!bar) return;
    evt.preventDefault();

    if (isTouch) return; // touchstart already opened it; nothing left to do

    var root = bar.closest('[data-custom-quick-add]');
    if (!root) return;

    var isOpen = root.classList.contains('custom-quick-add--open');
    resetAllExcept(root);
    root.classList.add('custom-quick-add--peek');
    root.classList.toggle('custom-quick-add--open', !isOpen);
  }

  // NOTE: image taps/clicks used to be intercepted here on the first
  // interaction (to force a "peek" before navigating). That's been
  // removed — a click on the product image now always navigates to
  // the product page immediately, on both touch and desktop. The
  // quick-add bar is still revealed on hover (desktop, CSS) or
  // touchstart (mobile, above) for anyone who wants to tap a size
  // instead of the image, since the bar sits outside the <a> link.

  function setStatus(root, show, text) {
    var status = root.querySelector('[data-cqa-status]');
    if (!status) return;

    if (show && text) {
      var textEl = status.querySelector('[data-cqa-status-text]');
      if (textEl) textEl.textContent = text;
    }

    root.classList.toggle('custom-quick-add--adding', !!show);
  }

  function handleSizeClick(evt) {
    var sizeBtn = evt.target.closest('[data-cqa-size]');
    if (!sizeBtn || sizeBtn.disabled) return;
    evt.preventDefault();
    evt.stopPropagation(); // FIXED: Prevent event bubbling

    var root = sizeBtn.closest('[data-custom-quick-add]');
    if (!root) return;

    // Guard: ignore any tap while a request for this card is already in flight,
    // so a fast double-tap can never send two add-to-cart requests.
    if (root.dataset.cqaAdding === '1') return;

    var variantId = sizeBtn.getAttribute('data-variant-id');
    if (!variantId) return;

    root.dataset.cqaAdding = '1';
    sizeBtn.disabled = true; // FIXED: Disable button immediately to prevent re-clicks
    setStatus(root, true, 'Adding...');

    fetch(ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var message = (data && (data.description || data.message)) || 'Could not add to cart';
            throw new Error(message);
          }
          return data;
        });
      })
      .then(function () {
        // Success — let the store's cart drawer app (Opus) detect the
        // standard cart/add.js request and open itself. Also notify any
        // theme listeners that the cart changed, in case other widgets
        // (cart item counters, etc.) want to refresh.
        document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: variantId } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { variantId: variantId } }));

        setStatus(root, false);
        root.classList.remove('custom-quick-add--open');
        root.classList.remove('custom-quick-add--peek');
        delete root.dataset.cqaJustPeeked;
        delete root.dataset.cqaAdding;
        sizeBtn.disabled = false; // FIXED: Re-enable button after success
      })
      .catch(function (error) {
        setStatus(root, true, (error && error.message) || 'Sold out');
        setTimeout(function () {
          setStatus(root, false);
        }, 1800);
        delete root.dataset.cqaAdding;
        sizeBtn.disabled = false; // FIXED: Re-enable button after error
      });
  }

  document.addEventListener('touchstart', handleTouchStart, { passive: true });

  document.addEventListener('click', function (evt) {
    if (evt.target.closest('[data-cqa-size]')) {
      handleSizeClick(evt);
      return;
    }
    if (evt.target.closest('[data-cqa-bar]')) {
      handleBarClick(evt);
      return;
    }
    // A click on the product image itself is no longer intercepted —
    // it navigates to the product page normally (see handleTouchStart
    // above for the touch peek-reveal, which no longer blocks this).
    // Tapped somewhere else entirely — reset any peeked/open cards
    if (!evt.target.closest('[data-custom-quick-add]') && !evt.target.closest('.image-cont .product-link')) {
      resetAllExcept(null);
    }
  }, true); // capture phase so size/bar taps are still caught before the link's own click

  document.addEventListener('keydown', function (evt) {
    if (evt.key !== 'Enter' && evt.key !== ' ') return;
    var bar = evt.target.closest && evt.target.closest('[data-cqa-bar]');
    if (!bar) return;
    evt.preventDefault();
    bar.click();
  });
})();